<template>
   <NuxtLayout :name="layout">
     <!-- Custom 404 page with CraftCMS content -->
     <div v-if="error && error.statusCode == 404 && errorPageData && components.length > 0"
       class="space-y-16"
       :class="components[0].__typename == 'carousel-block' ? '' : 'py-16'"
     >
       <div v-for="component in components" :key="component.id">
         <component :is="templates[component.__typename]" v-bind="component" :first="isFirstComponent(component)" ></component>
       </div>
     </div>
     <!-- Default error pages with centered layout -->
     <div v-else class="grid min-h-full place-content-center place-items-center bg-white px-6 py-24 sm:py-32 lg:px-8">
       <div class="text-center" v-if="error && error.statusCode == 503">
         <p class="text-lg font-semibold text-center text-primary-500">
           {{ error.statusCode }}
         </p>
         <h1 class="mt-4 text-2xl font-bold tracking-tight text-gray-900 sm:text-4xl">
           {{ $t(error.message) }}
         </h1>
         <p class="mt-4">
           {{ $t('Please try again in a few minutes.') }}
         </p>
       </div>
       <!-- Fallback 404 page -->
       <div class="text-center" v-else-if="error && error.statusCode == 404">
         <p class="text-lg font-semibold text-primary">
           {{ error.statusCode }}
         </p>
         <h1 class="mt-4 text-2xl font-bold tracking-tight text-gray-900 sm:text-5xl">
           {{ $t('Page not found') }}
         </h1>
         <p class="mt-6 text-base leading-7 text-gray-600">
           {{ $t("Sorry, we couldn't find the page you're looking for.") }}
         </p>
         <div class="mt-10 flex items-center justify-center gap-x-6">
           <NuxtLink
             :to="$localePath('/')"
             class="btn btn-primary"
           >
             {{ $t('Go back home') }}
           </NuxtLink>
         </div>
       </div>
       <div class="text-center" v-else>
         <p class="text-base font-semibold text-primary">
           {{ error }}
         </p>
         <div class="mt-10 flex items-center justify-center gap-x-6">
           <v-button :to="$localePath('/')">
             {{ $t('Go back home') }}
           </v-button>
         </div>
       </div>
     </div>
   </NuxtLayout>
</template>
<script setup lang="ts">
import { useSdk } from '~/sdk';

const props = defineProps({
  error: {
    type: Object,
    required: true,
  },
});

const config = useRuntimeConfig();
const { locale } = useI18n();

const isMaintenance = computed(() => {
  return config.public.maintenance_mode;
});

const layout = computed(() => {
  return isMaintenance.value ? 'maintenance' : 'default';
});

// Fetch custom 404 error page content from CraftCMS
const { data: errorPageData } = await useAsyncData('error-page-' + locale.value + '-404', async () => {
  if (props.error?.statusCode === 404) {
    try {
      return await useSdk().cms.getContent({
        entry: {
          section: 'errors_',
          slug: '404',
        },
      });
    } catch (e) {
      console.error('Failed to fetch 404 error page content:', e);
      return null;
    }
  }
  return null;
});

const components = computed(() => {
  return errorPageData.value?.components ?? [];
});

const templates: any = {
  'carousel-block': defineAsyncComponent(() => import('./components/BlockCarousel.vue')),
  'categories-block': defineAsyncComponent(() => import('./components/BlockCategories.vue')),
  'products-block': defineAsyncComponent(() => import('./components/BlockProducts.vue')),
  'features-block': defineAsyncComponent(() => import('./components/BlockFeatures.vue')),
  'categoryproducts-block': defineAsyncComponent(() => import('./components/promo/BlockCategoryProducts.vue')),
  'offer-block': defineAsyncComponent(() => import('./components/promo/BlockOffer.vue')),
  'faqs-block': defineAsyncComponent(() => import('./components/promo/BlockFaq.vue')),
  'textimage-block': defineAsyncComponent(() => import('./components/promo/BlockImageText.vue')),
  'text-block': defineAsyncComponent(() => import('./components/promo/BlockText.vue')),
  'cards-block': defineAsyncComponent(() => import('./components/BlockCards.vue')),
};

const isFirstComponent = (component: any) => {
  return components.value && components.value.length > 0 && components.value[0].id === component.id && component.__typename === 'text-block';
};
</script>
